import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { SimpleSelectField } from '@unite-us/app-components';
import {
  resolveFieldError,
  resolveSelectedValue,
} from './selectFieldHelpers';
import {
  callPrimaryWorkerBlur,
  callPrimaryWorkerChange,
  normalizeSearchInput,
  shouldFetchPrimaryWorkers,
  toPrimaryWorkerOptions,
} from './casePrimaryWorkerFieldHelpers';

const CasePrimaryWorkerField = ({
  fetchGroupsUsers,
  groupsUsers,
  field,
  meta,
  searchPlaceholderValue,
  ...props
}) => {
  const allPrimaryWorkerOptions = useMemo(
    () => toPrimaryWorkerOptions(groupsUsers),
    [groupsUsers],
  );

  const value = useMemo(
    () => resolveSelectedValue(field?.value),
    [field?.value],
  );

  const error = resolveFieldError(meta);
  const resolvedPlaceholder = props.placeholder || searchPlaceholderValue || '';

  const handleChange = useCallback(
    (selectedValue) => callPrimaryWorkerChange(field, selectedValue),
    [field],
  );

  const handleBlur = useCallback(
    () => callPrimaryWorkerBlur(field),
    [field],
  );

  const handleInputChange = useCallback((inputValue, actionMeta) => {
    const normalizedInput = normalizeSearchInput(inputValue);
    const action = actionMeta?.action;
    const shouldFetch = shouldFetchPrimaryWorkers(action, normalizedInput);

    if (shouldFetch) {
      fetchGroupsUsers(normalizedInput);
    }

    return inputValue;
  }, [fetchGroupsUsers]);

  return (
    <SimpleSelectField
      disabled={props.disabled}
      options={allPrimaryWorkerOptions}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      onInputChange={handleInputChange}
      placeholder={resolvedPlaceholder}
      searchable={props.searchChoices}
      clearable={false}
      required={props.required}
      error={error || undefined}
      inputId={props.id}
      {...props}
    />
  );
};

CasePrimaryWorkerField.propTypes = {
  fetchGroupsUsers: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
  field: PropTypes.object.isRequired,
  meta: PropTypes.object,
  groupsUsers: PropTypes.object.isRequired,
  id: PropTypes.string,
  inline: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  searchPlaceholderValue: PropTypes.string,
  searchChoices: PropTypes.bool,
};

CasePrimaryWorkerField.defaultProps = {
  id: 'primary-worker',
  inline: false,
  label: 'Primary Worker',
  placeholder: 'Choose one...',
  searchPlaceholderValue: 'Search...',
  searchChoices: true,
  required: true,
  meta: {},
};

export default CasePrimaryWorkerField;
